import { getChordNoteIndices } from '@/lib/pianoChords';

// Piano keyboard from C to E (2 octaves + 4 keys = ~2 octaves)
// White keys layout: C D E F G A B (7 per octave)
// Black keys between: C# D#  F# G# A#  (5 per octave)
const OCTAVES = 2;
const WHITE_KEYS_PER_OCT = 7;
const WHITE_KEYS = OCTAVES * WHITE_KEYS_PER_OCT + 1; // +1 for closing C
// White key semitone mapping: C=0 D=2 E=4 F=5 G=7 A=9 B=11
const WHITE_TO_SEMITONE = [0, 2, 4, 5, 7, 9, 11];
// Black key positions between white keys (as fractional white-key index).
// C# between C(0) & D(1), D# between D(1) & E(2), F# 3-4, G# 4-5, A# 5-6
const BLACK_POSITIONS = [
  { pos: 0.75, semitone: 1 },   // C#
  { pos: 1.75, semitone: 3 },   // D#
  { pos: 3.75, semitone: 6 },   // F#
  { pos: 4.75, semitone: 8 },   // G#
  { pos: 5.75, semitone: 10 },  // A#
];

export default function PianoChord({ name, size = 100 }) {
  const notes = getChordNoteIndices(name);
  if (!notes || notes.length === 0) {
    return (
      <div className="inline-flex flex-col items-center" style={{ width: size }}>
        <div className="w-full aspect-[2/1] rounded-lg border border-dashed border-stone-700 flex items-center justify-center text-stone-500 text-xs">?</div>
        <div className="text-xs font-medium text-stone-300 mt-1">{name}</div>
      </div>
    );
  }

  const w = size * 1.6;
  const h = size * 0.9;
  const whiteWidth = w / WHITE_KEYS;
  const blackWidth = whiteWidth * 0.6;
  const blackHeight = h * 0.62;

  // Determine which absolute semitones (0..24) correspond to which visible keys
  const rootNote = notes[0]; // could be < 0 (bass)
  // Shift the display so lowest note is around octave 4 (starts at C=0).
  const minNote = Math.min(...notes);
  const shift = minNote < 0 ? -minNote : 0; // shift up so no negative

  const highlighted = new Set(notes.map(n => n + shift));

  // Build white keys array
  const whiteKeys = [];
  for (let i = 0; i < WHITE_KEYS; i++) {
    const octave = Math.floor(i / WHITE_KEYS_PER_OCT);
    const inOct = i % WHITE_KEYS_PER_OCT;
    const semitone = octave * 12 + WHITE_TO_SEMITONE[inOct];
    whiteKeys.push({ x: i * whiteWidth, semitone });
  }

  // Build black keys
  const blackKeys = [];
  for (let oct = 0; oct < OCTAVES; oct++) {
    BLACK_POSITIONS.forEach(({ pos, semitone }) => {
      const x = (oct * WHITE_KEYS_PER_OCT + pos) * whiteWidth;
      blackKeys.push({ x, semitone: oct * 12 + semitone });
    });
  }

  return (
    <div className="inline-flex flex-col items-center" style={{ width: w }} data-testid={`piano-chord-${name}`}>
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="block">
        {/* White keys */}
        {whiteKeys.map((k, i) => {
          const isHi = highlighted.has(k.semitone);
          return (
            <rect
              key={`w${i}`}
              x={k.x}
              y={0}
              width={whiteWidth - 0.5}
              height={h}
              fill={isHi ? '#c2410c' : '#fafaf9'}
              stroke="#57534e"
              strokeWidth={0.5}
              rx={2}
            />
          );
        })}
        {/* Black keys */}
        {blackKeys.map((k, i) => {
          const isHi = highlighted.has(k.semitone);
          return (
            <rect
              key={`b${i}`}
              x={k.x}
              y={0}
              width={blackWidth}
              height={blackHeight}
              fill={isHi ? '#9a3412' : '#1c1917'}
              stroke="#0c0a09"
              strokeWidth={0.5}
              rx={1.5}
            />
          );
        })}
      </svg>
      <div className="text-xs font-display text-stone-100 mt-1">{name}</div>
    </div>
  );
}
